import { atom } from "jotai";
import type { ChatMember } from "shared";

// ─── Drawer State ───────────────────────────────────────────────────

const chatMembersDrawerOpenAtom = atom(false);

export const isChatMembersDrawerOpen = atom((get) =>
  get(chatMembersDrawerOpenAtom),
);

export const setChatMembersDrawerOpen = atom(
  null,
  (_get, set, value: boolean) => {
    set(chatMembersDrawerOpenAtom, value);
  },
);

// ─── Selection State ────────────────────────────────────────────────

const selectedMemberIdsAtom = atom<Set<string>>(new Set<string>());

export const selectedMemberIds = atom((get) => get(selectedMemberIdsAtom));

export const setSelectedMemberIds = atom(
  null,
  (_get, set, value: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    if (typeof value === "function") {
      set(selectedMemberIdsAtom, value(_get(selectedMemberIdsAtom)));
    } else {
      set(selectedMemberIdsAtom, value);
    }
  },
);

export const toggleMemberSelection = atom(
  null,
  (get, set, userId: string) => {
    const current = new Set(get(selectedMemberIdsAtom));
    if (current.has(userId)) {
      current.delete(userId);
    } else {
      current.add(userId);
    }
    set(selectedMemberIdsAtom, current);
  },
);

// ─── Pending Members (newly selected, not yet saved) ────────────────

const pendingMembersMapAtom = atom<Map<string, ChatMember>>(new Map());

export const pendingMembersMap = atom((get) => get(pendingMembersMapAtom));

/** Stores a full ChatMember for later use during save and toggles selection on */
export const cachePendingMember = atom(
  null,
  (get, set, member: ChatMember) => {
    const current = new Map(get(pendingMembersMapAtom));
    current.set(member.user_id, member);
    set(pendingMembersMapAtom, current);
    const selectedIds = new Set(get(selectedMemberIdsAtom));
    selectedIds.add(member.user_id);
    set(selectedMemberIdsAtom, selectedIds);
  },
);

export const clearMemberSelection = atom(null, (_get, set) => {
  set(selectedMemberIdsAtom, new Set());
});

// ─── Search State ───────────────────────────────────────────────────

const memberSearchQueryAtom = atom("");

export const memberSearchQuery = atom((get) => get(memberSearchQueryAtom));

export const setMemberSearchQuery = atom(
  null,
  (_get, set, value: string) => {
    set(memberSearchQueryAtom, value);
  },
);

// ─── Members Cache (local state for drawer) ─────────────────────────

const allMembersAtom = atom<ChatMember[]>([]);

export const allMembers = atom((get) => get(allMembersAtom));

export const setAllMembers = atom(
  null,
  (_get, set, value: ChatMember[] | ((prev: ChatMember[]) => ChatMember[])) => {
    if (typeof value === "function") {
      set(allMembersAtom, value(_get(allMembersAtom)));
    } else {
      set(allMembersAtom, value);
    }
  },
);

// ─── Filtered Members (derived atom) ────────────────────────────────

export const filteredMembers = atom((get) => {
  const members = get(allMembersAtom);
  const searchQuery = get(memberSearchQueryAtom).toLowerCase().trim();

  if (!searchQuery) {
    return members;
  }

  return members.filter(
    (member) =>
      (member.display_name ?? "").toLowerCase().includes(searchQuery) ||
      (member.email ?? "").toLowerCase().includes(searchQuery),
  );
});

// ─── Selected Members (derived atom) ────────────────────────────────

export const selectedMembers = atom((get) => {
  const members = get(allMembersAtom);
  const selectedIds = get(selectedMemberIdsAtom);

  return members.filter((member) => selectedIds.has(member.user_id));
});

// ─── External Emails State ──────────────────────────────────────────

const externalEmailsAtom = atom<string[]>([]);

export const externalEmails = atom((get) => get(externalEmailsAtom));

export const addExternalEmail = atom(null, (get, set, email: string) => {
  const current = get(externalEmailsAtom);
  const normalized = email.toLowerCase();
  if (!current.includes(normalized)) {
    set(externalEmailsAtom, [...current, normalized]);
  }
});

export const removeExternalEmail = atom(null, (get, set, email: string) => {
  set(
    externalEmailsAtom,
    get(externalEmailsAtom).filter((e) => e !== email),
  );
});

// ─── Available Customer Members Status (set by AvailableMembersList) ─

const availableCustomerMembersCountAtom = atom(0);
const isCustomerMembersSearchingAtom = atom(false);

export const availableCustomerMembersCount = atom((get) =>
  get(availableCustomerMembersCountAtom),
);

export const isCustomerMembersSearching = atom((get) =>
  get(isCustomerMembersSearchingAtom),
);

export const setAvailableCustomerMembersCount = atom(
  null,
  (_get, set, count: number) => {
    set(availableCustomerMembersCountAtom, count);
  },
);

export const setIsCustomerMembersSearching = atom(
  null,
  (_get, set, value: boolean) => {
    set(isCustomerMembersSearchingAtom, value);
  },
);

// ─── Scope Changes State (thread ↔ ticket level) ───────────────────

/** Map of userId → new level ("ticket" | "thread") for members whose scope changed */
const scopeChangesAtom = atom<Map<string, "ticket" | "thread">>(new Map());

export const scopeChanges = atom((get) => get(scopeChangesAtom));

export const setMemberLevel = atom(
  null,
  (get, set, payload: { userId: string; level: "ticket" | "thread" }) => {
    const current = new Map(get(scopeChangesAtom));
    const members = get(allMembersAtom);
    const member = members.find((m) => m.user_id === payload.userId);
    const originalLevel =
      member?.member_source === "cc_thread" ? "thread" : "ticket";

    if (payload.level === originalLevel) {
      current.delete(payload.userId);
    } else {
      current.set(payload.userId, payload.level);
    }
    set(scopeChangesAtom, current);
  },
);

// ─── Pending Changes Detection (derived atom) ───────────────────────

const initialMemberIdsAtom = atom<Set<string>>(new Set<string>());

export const setInitialMemberIds = atom(
  null,
  (_get, set, value: Set<string>) => {
    set(initialMemberIdsAtom, value);
  },
);

export const hasPendingChanges = atom((get) => {
  const initial = get(initialMemberIdsAtom);
  const current = get(selectedMemberIdsAtom);
  const extEmails = get(externalEmailsAtom);
  const changes = get(scopeChangesAtom);

  if (extEmails.length > 0) return true;
  if (changes.size > 0) return true;
  if (initial.size !== current.size) return true;

  for (const id of current) {
    if (!initial.has(id)) return true;
  }

  return false;
});

// ─── Reset Pending Changes (keeps allMembers + initialIds intact) ───

export const resetPendingChanges = atom(null, (get, set) => {
  set(selectedMemberIdsAtom, new Set(get(initialMemberIdsAtom)));
  set(memberSearchQueryAtom, "");
  set(externalEmailsAtom, []);
  set(scopeChangesAtom, new Map());
  set(pendingMembersMapAtom, new Map());
  set(availableCustomerMembersCountAtom, 0);
  set(isCustomerMembersSearchingAtom, false);
});

// ─── Reset All State ────────────────────────────────────────────────

export const resetChatMembersDrawer = atom(null, (_get, set) => {
  set(chatMembersDrawerOpenAtom, false);
  set(selectedMemberIdsAtom, new Set());
  set(memberSearchQueryAtom, "");
  set(allMembersAtom, []);
  set(initialMemberIdsAtom, new Set());
  set(externalEmailsAtom, []);
  set(scopeChangesAtom, new Map());
  set(pendingMembersMapAtom, new Map());
  set(availableCustomerMembersCountAtom, 0);
  set(isCustomerMembersSearchingAtom, false);
});
